import Delay from '@/models/Delay';
import DelayService from '@/services/DelayService';
import { defineStore } from 'pinia';
import { useRepo } from 'pinia-orm';
import { useCommentsStore } from './comments';
import { useCorrespondencesStore } from './correspondences';

export const useDelaysStore = defineStore('delays', {
  state: () => ({}),

  getters: {
    models: () => useRepo(Delay),
  },

  actions: {
    async fetchDelays(projectId: Id, form: ApiRequests['api.v1.delays.index'] = {}) {
      const [data, error] = await DelayService.index(projectId, form);

      if(error) {
        throw error;
      }

      this.models.save(data.data);

      return data;
    },

    // async fetchApprovedVariationOrders(projectId: Id) {
    //   const [data, error] = await VariationOrderService.claimIndex(projectId);

    //   if(data) {
    //     this.models.save(data);

    //     return data;
    //   }

    //   throw error;
    // },

    async fetchDelay(id: Id) {
      const [data, error] = await DelayService.show(id);

      if(error) {
        throw error;
      }

      this.models.save(data.data);

      return data;
    },

    async acceptDelay(id: Id) {
      const [data, error] = await DelayService.accept(id);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async declineDelay(id: Id) {
      const [data, error] = await DelayService.decline(id);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async submitDelay(id: Id, form: ApiRequests['api.v1.delays.submit']) {
      const [data, error] = await DelayService.submit(id, form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async provisionDelay(id: Id, signedParams: unknown) {
      const [data, error] = await DelayService.provision(id, signedParams);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async declineSubmission(id: Id, signedParams: unknown, form: ApiRequests['api.v1.delays.decline-submission']) {
      const [data, error] = await DelayService.declineSubmission(id, signedParams, form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async deleteDelay(id: Id) {
      const [data, error] = await DelayService.delete(id);

      if(error) {
        throw error;
      }

      this.models.destroy(id);

      return data;
    },

    async approveDelay(id: Id, form: ApiRequests['api.v1.delays.approve']) {
      const [data, error] = await DelayService.approve(id, form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async completeDelay(id: Id) {
      const [data, error] = await DelayService.complete(id);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async createDelay(projectId: Id, request: ApiRequests['api.v1.delays.store'] | FormData) {
      const [data, error] = await DelayService.create(projectId, request);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async updateDelay(id: Id, request: ApiRequests['api.v1.delays.update']) {
      const [data, error] = await DelayService.update(id, request);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async updateNextActionDelay(id: Id, form: ApiRequests['api.v1.delays.next-action']) {
      const [data, error] = await DelayService.nextAction(id, form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },
    // async deleteDelay(id: Id) {
    //   const [data, error] = await DelayService.delete(id);

    //   if(data) {
    //     this.models.destroy(data.id);

    //     return data;
    //   }

    //   throw error;
    // },

    async fetchDelaysSummary(projectId: Id) {
      const [data, error] = await DelayService.summary(projectId);

      if(error) {
        throw error;
      }

      return data as DelaysSummary;
    },

    async comment(id: Id, form: ApiRequests['api.v1.delays.comments']) {
      const commentsStore = useCommentsStore();
      const [data, error] = await DelayService.comment(id, form);

      if(error) {
        throw error;
      }

      commentsStore.models.save(data);

      return data;
    },

    async attachVariationOrder(id: Id, form: ApiRequests['api.v1.delays.attach-variation-order']) {
      const [data, error] = await DelayService.attachVariationOrder(id, form);

      if(error) {
        throw error;
      }

      return data;
    },

    async media(id: Id) {
      const [data, error] = await DelayService.media(id);

      if(error) {
        throw error;
      }

      return data;
    },

    async storeMedia(id: Id, form: ApiRequests['api.v1.claims.store-media'] | FormData) {
      const [data, error] = await DelayService.storeMedia(id, form);

      if(error) {
        throw error;
      }

      return data;
    },

    async storeNotice(id: Id, form: ApiRequests['api.v1.delays.notice.store'] | FormData) {
      const [data, error] = await DelayService.notice.store(id, form);

      if(error) {
        throw error;
      }

      this.models.save(data.delay);

      return data;
    },

    async storeValueNotice(id: Id, form: ApiRequests['api.v1.delays.value-notice.store'] | FormData) {
      const [data, error] = await DelayService.valueNotice.store(id, form);

      if(error) {
        throw error;
      }

      this.models.save(data.delay);

      return data;
    },

    async fetchCorrespondences(id: Id) {
      const correspondencesStores = useCorrespondencesStore();
      const [data, error] = await DelayService.correspondence.index(id);

      if(error) {
        throw error;
      }

      correspondencesStores.models.save(data);

      return data;
    },

    async storeCorrespondence(id: Id, form: ApiRequests['api.v1.delays.correspondence.store'] | FormData) {
      const correspondencesStores = useCorrespondencesStore();
      const [data, error] = await DelayService.correspondence.store(id, form);

      if(error) {
        throw error;
      }

      correspondencesStores.models.save(data.correspondence);

      return data;
    },

    async sendNoticeForDelay(id: Id, form: ApiRequests['api.v1.delays.send-notice']) {
      const [data, error] = await DelayService.sendNotice(id, form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },
  },
});
